/**
 * PostHog feature-flag keys consumed by the Pro CRM frontend.
 *
 * Keys are runtime flags evaluated by the PostHog JS SDK (wired in `main.tsx`).
 * Read them with `useFeatureFlagEnabled(KEY)` from `@posthog/react`, which
 * returns `true | false | undefined`. Gate features that should stay hidden
 * until explicitly launched on `=== true` so they fail closed while the flag is
 * absent or still loading.
 */

/**
 * Gates the "Public founding directory" surface (the opt-in card on the pro
 * profile). Default-closed: the directory is hidden until this flag resolves
 * `true` for the pro. Kept off until the public Founding Agents page ships.
 */
export const FOUNDING_DIRECTORY_FLAG_KEY = "founding_directory_enabled";

/**
 * Gates the Pro CRM Copilot (the AI chat dock, its header opener pill, and the
 * `/assistant` full-page surface — plan 188). Default-closed in prod: every
 * copilot surface stays hidden until this flag resolves `true` for the pro, so
 * a controlled PostHog rollout governs who gets the beta assistant and the
 * flag doubles as a kill-switch. Read it via `useCopilotEnabled`, which ORs in
 * `import.meta.env.DEV` so local dev + preview stacks (which have no PostHog
 * flag) keep the copilot fully usable.
 */
export const PRO_CRM_COPILOT_FLAG_KEY = "pro_crm_copilot_enabled";

/**
 * Gates the Sill Pro launch independently of Marketplace. Free agents do not
 * see Sill Pro subscription controls until this resolves true, and the backend
 * independently checks the same flag before creating Checkout. Existing
 * subscribers keep subscription management access regardless of the flag so a
 * rollout pause cannot strand a paying agent.
 */
export const SILL_PRO_FLAG_KEY = "sill_pro_enabled";

/**
 * Gates the ENTIRE Pro CRM Marketplace surface — the "Marketplace" header
 * entry, the pageSidebar mini-app, every `/marketplace/*` route, and the
 * connection-fee rate card on the marketplace Profile page. Default-closed:
 * read via `useMarketplaceEnabled`, which ORs in `import.meta.env.DEV` so local
 * dev + preview stay usable without a flag.
 *
 * This deliberately REUSES the consumer go-live flag key
 * (`marketplace_go_live_enabled`, the buyer-side `useMarketplaceState` gate) so
 * ONE PostHog switch flips buyer- and agent-facing marketplace together — the
 * single "marketplace is live" lever. Partition into a pro-specific flag later
 * if the two rollouts ever need to diverge.
 *
 * NOTE: the backend billing kill-switch (`MARKETPLACE_BILLING_ENABLED`, default
 * ON) is a separate server-side env lever — the Go backend does not evaluate
 * PostHog flags. The marketplace card-on-file Settings section follows this
 * go-live flag; Sill Pro Checkout has its own independent rollout flag.
 */
export const MARKETPLACE_FLAG_KEY = "marketplace_go_live_enabled";

/**
 * Hides the Pro marketplace surface from LOAN OFFICERS until the lender side
 * of the marketplace is live. Lenders still sign up at /join and get the CRM;
 * only the marketplace entry points (nav, Home setup/marketplace heroes,
 * banners, `/marketplace/*`) are withheld. Agents never read this key — they
 * follow `marketplace_go_live_enabled` alone. Fail-closed in prod: an LO sees
 * marketplace only on an explicit `true`. Read via `useLenderMarketplaceGate`.
 */
export const LENDER_MARKETPLACE_FLAG_KEY = "lender_marketplace_enabled";
